/*
 * Der Entwurf einer Anfrage — mitsamt den Anhängen.
 *
 * Warum überhaupt: Wer ein Bildschirmfoto von einer **anderen** Seite der
 * Anwendung mitschicken will, muss das Fenster schließen, dorthin wechseln, das
 * Bild machen und zurückkommen. Ginge dabei alles verloren, wäre genau der Fall
 * unmöglich, für den der Kanal gedacht ist: mehrere Aufnahmen zu einem Fehler.
 *
 * **IndexedDB und nicht `sessionStorage`**, weil Dateien mitmüssen: Der
 * Sitzungsspeicher nimmt nur Zeichenketten, ein 5-MB-Bild als Base64 wäre rund
 * 6,7 MB — schon eine einzige Datei sprengt das übliche Kontingent. IndexedDB
 * legt `File`-Objekte unverändert ab.
 *
 * Ohne IndexedDB (sehr alter Browser, privater Modus mancher Browser) fällt der
 * Speicher auf das Fenster zurück: Der Entwurf überlebt dann das Schließen des
 * Fensters, aber kein Neuladen. Das ist der wichtigere der beiden Fälle — und
 * allemal besser, als das Formular deswegen gar nicht anzubieten.
 */

const DB_NAME = 'junior-hilfe'
const STORE = 'entwuerfe'
const KEY = 'aktuell'

// Rückfallebene ohne IndexedDB. Bewusst ein Modul-Zustand und kein Fenster-
// Attribut: Er soll mit dem Programmstand verschwinden, nicht darüber hinaus.
let imSpeicher = null

const idb = () => (typeof indexedDB === 'undefined' ? null : indexedDB)

const oeffne = () =>
	new Promise((resolve, reject) => {
		const db = idb()
		if (!db) {
			reject(new Error('IndexedDB steht nicht zur Verfügung'))
			return
		}

		const anfrage = db.open(DB_NAME, 1)
		anfrage.onupgradeneeded = () => {
			if (!anfrage.result.objectStoreNames.contains(STORE)) {
				anfrage.result.createObjectStore(STORE)
			}
		}
		anfrage.onsuccess = () => resolve(anfrage.result)
		anfrage.onerror = () => reject(anfrage.error)
	})

const inTransaktion = (modus, arbeit) =>
	oeffne().then(
		db =>
			new Promise((resolve, reject) => {
				const tx = db.transaction(STORE, modus)
				const anfrage = arbeit(tx.objectStore(STORE))
				anfrage.onsuccess = () => resolve(anfrage.result)
				anfrage.onerror = () => reject(anfrage.error)
				tx.oncomplete = () => db.close()
			})
	)

/** Entwurf ablegen. Wirft nie — ein misslungenes Speichern darf nichts abbrechen. */
export const speichereEntwurf = async entwurf => {
	const wert = { ...entwurf, gespeichertAm: new Date().toISOString() }
	imSpeicher = wert

	try {
		await inTransaktion('readwrite', store => store.put(wert, KEY))
	} catch (error) {
		// Der Entwurf lebt dann nur im Fenster weiter — siehe oben.
	}

	return wert
}

/** Der abgelegte Entwurf oder `null`. */
export const liesEntwurf = async () => {
	try {
		const wert = await inTransaktion('readonly', store => store.get(KEY))
		if (wert) return wert
	} catch (error) {
		// weiter mit dem Fensterspeicher
	}

	return imSpeicher
}

/** Entwurf verwerfen — nach dem Absenden und auf ausdrücklichen Wunsch. */
export const verwirfEntwurf = async () => {
	imSpeicher = null

	try {
		await inTransaktion('readwrite', store => store.delete(KEY))
	} catch (error) {
		// nichts zu tun
	}
}

/** Trägt der Entwurf überhaupt etwas? Ein leeres Formular ist kein Entwurf. */
export const hatInhalt = entwurf =>
	Boolean(entwurf && (entwurf.titel?.trim() || entwurf.beschreibung?.trim() || entwurf.dateien?.length))
